import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { compact, orderBy } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cleanAmountForAPI } from '@/lib/utils'
import axiosInstance from '@/request/axios'
import { cpnService } from '@/services/cpn'
import { useSendLimitsStore } from '@/stores/sendLimitsStore'
import {
  CPN_PAYMENT_METHODS,
  CpnPaymentMethod,
  CpnPaymentReason,
  transformConfigurationsToDynamicRoutes,
  validateCurrencyCountryPaymentMethodFromConfigs,
} from '@/utils/cpn'
import { cadToDestinationRate } from '@/utils/currencyUtils'
import { CPN_ERROR_CODES, getCpnErrorCode } from '@/utils/errorFormatting'
import { logger } from '@/utils/logger'

export const CPN_QUERY_KEYS = {
  quotes: ['cpn', 'quotes'] as const,
  latestQuote: (params: {
    quoteType: 'fromExact' | 'toExact'
    blockchain?: string
    senderCountry: string
    sourceCurrency: string
    amount: string
    destinationCountry: string
    destinationCurrency: string
    paymentMethod?: string
  }) => ['cpn', 'latest-quote', params] as const,
  quote: (id: string) => ['cpn', 'quote', id] as const,
  beneficiaries: (userId: string) => ['cpn', 'beneficiaries', userId] as const,
  beneficiary: (id: string) => ['cpn', 'beneficiary', id] as const,
  payments: (userId: string, active?: boolean) =>
    ['cpn', 'payments', userId, active] as const,
  payment: (id: string) => ['cpn', 'payment', id] as const,
  paymentStatus: (id: string) => ['cpn', 'payment-status', id] as const,
  requirements: (params: {
    quoteId: string
    beneficiaryUuid: string
    magicWord?: string
  }) => ['cpn', 'requirements', params] as const,
  transactions: (paymentId: string, active?: boolean) =>
    ['cpn', 'transactions', paymentId, active] as const,
  rfis: (userId: string) => ['cpn', 'rfis', userId] as const,
  swapStatus: (intentId: string) => ['cpn', 'swap-status', intentId] as const,
  configurations: (params: {
    sourceCurrency: string
    destinationCountry: string
    destinationCurrency: string
  }) => ['cpn', 'configurations', params] as const,
  allConfigurations: () => ['cpn', 'all-configurations'] as const,
  user: () => ['user'] as const,
}

// Hook for real-time quotes
export const useLatestQuote = (
  params: {
    quoteType: 'fromExact' | 'toExact'
    blockchain?: string
    senderCountry: string
    sourceCurrency: string
    amount: string
    destinationCountry: string
    destinationCurrency: string
    paymentMethod?: string
  },
  options?: {
    enabled?: boolean
    refetchInterval?: number
  }
) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.latestQuote(params),
    queryFn: async () => {
      try {
        const result = await cpnService.getLatestQuote(params)
        return result
      } catch (error) {
        const errorCode = getCpnErrorCode(error?.message || '')
        if (errorCode === CPN_ERROR_CODES.BFI_UNAVAILABLE) {
          Sentry.metrics.count('cpn.bfi_unavailable', 1, {
            attributes: {
              error_code: errorCode,
              destination_country: params.destinationCountry,
              destination_currency: params.destinationCurrency,
              payment_method: params.paymentMethod,
            },
          })
        }
        throw error
      }
    },
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    retry: (failureCount, error: any) => {
      // Don't retry validation errors (400-499) - show them immediately
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false
      }
      // For 500 errors, check if it's a validation error in the message
      if (
        error?.message?.includes('Quote validation error') ||
        error?.response?.data?.code === 'AMOUNT_OUT_OF_RANGE'
      ) {
        return false
      }
      // Retry server errors (500+) up to 3 times
      const shouldRetry = failureCount < 3
      return shouldRetry
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Hook for getting a specific quote
export const useQuote = (quoteId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.quote(quoteId),
    queryFn: () => cpnService.getQuote(quoteId),
    enabled: enabled && !!quoteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook for beneficiary management
export const useBeneficiaries = (
  userId: string,
  options?: {
    enabled?: boolean
    filterCountry?: string
  }
) => {
  const enabled = options?.enabled ?? true
  const filterCountry = options?.filterCountry

  return useQuery({
    queryKey: CPN_QUERY_KEYS.beneficiaries(userId),
    queryFn: () => cpnService.getBeneficiaryUsers(),
    enabled: enabled && !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => {
      if (!filterCountry) {
        return data
      }

      // Sort: matching countries first, then most recent beneficiaries
      const sorted = orderBy(
        compact(data.beneficiaryUsers).reverse(),
        [(b) => b.country === filterCountry],
        ['desc']
      )

      return {
        ...data,
        beneficiaryUsers: sorted,
      }
    },
  })
}

export const useBeneficiary = (beneficiaryId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.beneficiary(beneficiaryId),
    queryFn: () => cpnService.getBeneficiaryUser(beneficiaryId),
    enabled: enabled && !!beneficiaryId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export const useCreateBeneficiary = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string
      data: {
        firstName: string
        lastName: string
        street: string
        city: string
        stateProvince: string
        postalCode: string
        country: string
        isBusiness: boolean
        isSelf: boolean
        paymentReason: CpnPaymentReason
      }
    }) => cpnService.createBeneficiaryUser(data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: CPN_QUERY_KEYS.beneficiaries(variables.userId),
      })
      if (data.beneficiaryUuid) {
        queryClient.invalidateQueries({
          queryKey: CPN_QUERY_KEYS.beneficiary(data.beneficiaryUuid),
        })
      }
    },
  })
}

export const useUpdateBeneficiaryMetadata = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      beneficiaryId,
      metadata,
    }: {
      beneficiaryId: string
      metadata: Record<string, unknown>
    }) => cpnService.updateBeneficiaryMetadata(beneficiaryId, metadata),
    onSuccess: (data, variables) => {
      // Invalidate the specific beneficiary
      queryClient.invalidateQueries({
        queryKey: CPN_QUERY_KEYS.beneficiary(variables.beneficiaryId),
      })
      // Also invalidate any beneficiaries lists that might include this beneficiary
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'beneficiaries'],
      })
    },
  })
}

export const useUpdateBeneficiary = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      beneficiaryId,
      data,
    }: {
      beneficiaryId: string
      data: {
        updateType: 'user' | 'metadata'
        firstName?: string
        lastName?: string
        street?: string
        city?: string
        stateProvince?: string
        postalCode?: string
        country?: string
        metadata?: Record<string, unknown>
      }
    }) =>
      axiosInstance.put(`/cpn/beneficiary-user?user_id=${beneficiaryId}`, data),
    onSuccess: (data, variables) => {
      // Invalidate the specific beneficiary
      queryClient.invalidateQueries({
        queryKey: CPN_QUERY_KEYS.beneficiary(variables.beneficiaryId),
      })
      // Invalidate all beneficiaries lists
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'beneficiaries'],
      })
    },
  })
}

export const useDeleteBeneficiary = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ beneficiaryId }: { beneficiaryId: string }) =>
      axiosInstance.delete(`/cpn/beneficiary-user?user_id=${beneficiaryId}`),
    onSuccess: (data, variables) => {
      // Remove the specific beneficiary from cache
      queryClient.removeQueries({
        queryKey: CPN_QUERY_KEYS.beneficiary(variables.beneficiaryId),
      })
      // Invalidate all beneficiaries lists
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'beneficiaries'],
      })
    },
  })
}

export const useTravelRule = (enabled = true) => {
  return useQuery({
    queryKey: ['cpn', 'travel-rule'],
    queryFn: () =>
      axiosInstance.get('/cpn/travel-rule').then((response) => response.data),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - travel rule data doesn't change often
  })
}

export const useUpdateTravelRule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      metadata,
      beneficiaryUuid,
    }: {
      metadata: Record<string, string>
      beneficiaryUuid?: string
    }) => axiosInstance.put('/cpn/travel-rule', { metadata, beneficiaryUuid }),
    onSuccess: () => {
      // Invalidate travel rule queries
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'travel-rule'],
      })
    },
  })
}

// Hook for payments
export const usePayments = (
  userId: string,
  active?: boolean,
  enabled = true
) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.payments(userId, active),
    queryFn: () => cpnService.getPayments(active),
    enabled: enabled && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes for payments (more dynamic)
  })
}

export const usePayment = (paymentId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.payment(paymentId),
    queryFn: () => cpnService.getPayment(paymentId),
    enabled: enabled && !!paymentId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

export const usePaymentStatus = (paymentId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.paymentStatus(paymentId),
    queryFn: () => cpnService.getPaymentStatusWithIntentUUID(paymentId),
    enabled: enabled && !!paymentId,
    refetchInterval: 30000, // Check status every 30 seconds
    staleTime: 15000, // Consider stale after 15 seconds
  })
}

export const useCreatePayment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: cpnService.createPayment,
    onSuccess: (data) => {
      // Invalidate payments lists
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'payments'],
      })
      // Set the new payment in cache
      if (data.paymentId) {
        queryClient.invalidateQueries({
          queryKey: CPN_QUERY_KEYS.payment(data.paymentId),
        })
        queryClient.invalidateQueries({
          queryKey: CPN_QUERY_KEYS.paymentStatus(data.paymentId),
        })
      }
    },
  })
}

// Hook for payment requirements
export const usePaymentRequirements = (
  quoteId: string,
  beneficiaryUuid: string,
  enabled = true
) => {
  const params = { quoteId, beneficiaryUuid }

  return useQuery({
    queryKey: CPN_QUERY_KEYS.requirements(params),
    queryFn: () => cpnService.getPaymentRequirements(quoteId, beneficiaryUuid),
    enabled: enabled && !!quoteId && !!beneficiaryUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook for transactions
export const useTransactions = (
  paymentId: string,
  active?: boolean,
  enabled = true
) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.transactions(paymentId, active),
    queryFn: () => cpnService.getTransactions(paymentId, active),
    enabled: enabled && !!paymentId,
    refetchInterval: 30000, // Check every 30 seconds
    staleTime: 15000,
  })
}

// Hook for RFIs
export const useRfis = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.rfis(userId),
    queryFn: () => cpnService.getRfis(),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useSubmitRfi = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      rfiId,
      paymentId,
      rfiData,
    }: {
      rfiId: string
      paymentId: string
      rfiData: Array<{ name: string; value: unknown }>
    }) => cpnService.submitRfi(rfiId, paymentId, rfiData),
    onSuccess: (data, variables) => {
      // Invalidate RFIs and payment status
      queryClient.invalidateQueries({
        queryKey: ['cpn', 'rfis'],
      })
      queryClient.invalidateQueries({
        queryKey: CPN_QUERY_KEYS.paymentStatus(variables.paymentId),
      })
    },
  })
}

// Hook for support tickets
export const useCreateSupportTicket = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: cpnService.createSupportTicket,
    onSuccess: (data, variables) => {
      // Optionally invalidate related queries
      queryClient.invalidateQueries({
        queryKey: CPN_QUERY_KEYS.paymentStatus(variables.paymentId),
      })
    },
    onError: (error) => {
      logger.error('Failed to create support ticket', error)
    },
  })
}

// Single hook for managing transfer state with smart quote handling
export const useTransferWithQuotes = (
  initialTransferData: {
    sendAmount: string
    sendCurrency: string
    receiveAmount: string
    receiveCountry: string
    receiveCurrency: string
    paymentMethod: CpnPaymentMethod
    fees: number
    exchangeRate?: number
  },
  options?: {
    enabled?: boolean
  }
) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const activeFieldRef = useRef<'send' | 'receive' | null>(null)
  const [debouncedAmount, setDebouncedAmount] = useState('1000.00')
  const [transferData, setTransferData] = useState(initialTransferData)
  const [quoteMode, setQuoteMode] = useState<'fromExact' | 'toExact' | 'none'>(
    'fromExact'
  )

  const isUpdatingFromQuoteRef = useRef(false)
  const { setLimits } = useSendLimitsStore()

  // Get dynamic routes data for fallback default payment method
  const { data: dynamicRoutes } = useDynamicSupportedRoutes()

  // Helper function to get default payment method from dynamic data
  const getDefaultPaymentMethodFromDynamicRoutes = useCallback(
    (
      countryCode: string,
      currencyCode: string
    ): CpnPaymentMethod | undefined => {
      const route = dynamicRoutes?.[countryCode]
      const currencyConfig = route?.currencies.find(
        (currency) => currency.code === currencyCode
      )
      return currencyConfig?.defaultPaymentMethod
    },
    [dynamicRoutes]
  )

  // Debounce the active amount based on quote mode
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // If we're updating from quote response, don't debounce
    if (isUpdatingFromQuoteRef.current) {
      return
    }

    const activeAmount =
      quoteMode === 'fromExact'
        ? transferData.sendAmount
        : transferData.receiveAmount

    debounceTimerRef.current = setTimeout(() => {
      const cleanAmount = cleanAmountForAPI(activeAmount)
      setDebouncedAmount(cleanAmount)
    }, 500)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [transferData.sendAmount, transferData.receiveAmount, quoteMode])

  // Use API-based validation for route validation
  const validationResult = useValidateRoute(
    {
      country: transferData.receiveCountry,
      currency: transferData.receiveCurrency,
      paymentMethod: transferData.paymentMethod,
    },
    {
      enabled:
        !!transferData.receiveCountry &&
        !!transferData.receiveCurrency &&
        !!transferData.paymentMethod,
    }
  )

  // Auto-correct currency and payment method when country changes
  useEffect(() => {
    if (
      !validationResult.isLoading &&
      !validationResult.isValid &&
      validationResult.suggestedCurrency
    ) {
      setTransferData((prev) => ({
        ...prev,
        receiveCurrency:
          validationResult.suggestedCurrency || prev.receiveCurrency,
        paymentMethod:
          validationResult.suggestedPaymentMethod ||
          getDefaultPaymentMethodFromDynamicRoutes(
            prev.receiveCountry,
            validationResult.suggestedCurrency || prev.receiveCurrency
          ) ||
          prev.paymentMethod,
      }))
    }
  }, [
    validationResult,
    transferData.receiveCountry,
    getDefaultPaymentMethodFromDynamicRoutes,
  ])

  // Single quote query that switches type based on mode
  const quoteParams = {
    quoteType: quoteMode as 'fromExact' | 'toExact',
    senderCountry: 'CA',
    sourceCurrency: 'CAD',
    amount: debouncedAmount,
    destinationCountry: transferData.receiveCountry,
    destinationCurrency: transferData.receiveCurrency,
    ...(transferData.paymentMethod &&
    CPN_PAYMENT_METHODS.includes(transferData.paymentMethod as CpnPaymentMethod)
      ? { paymentMethod: transferData.paymentMethod }
      : {}),
  }

  const {
    data: quoteData,
    isLoading: isQuoteLoading,
    error: quoteError,
  } = useLatestQuote(quoteParams, {
    enabled:
      options?.enabled !== false &&
      quoteMode !== 'none' &&
      !!debouncedAmount &&
      parseFloat(debouncedAmount) > 0 &&
      !!transferData.receiveCountry &&
      !!transferData.receiveCurrency,
  })

  // Update limits when quote data arrives
  useEffect(() => {
    if (quoteData?.limits) {
      setLimits(quoteData.limits.min, quoteData.limits.max)
    }
  }, [quoteData?.limits, setLimits])

  // Update opposite field when quote comes back (only if user isn't editing that field)
  useEffect(() => {
    const cpnQuotes = quoteData?.cpn
    const selectedQuote =
      cpnQuotes?.find(
        (quote) => quote.paymentMethod === transferData.paymentMethod
      ) || cpnQuotes?.[0]

    if (
      selectedQuote &&
      quoteData?.paytrie &&
      !isUpdatingFromQuoteRef.current
    ) {
      const quote = selectedQuote
      const paytrieData = quoteData.paytrie

      const destinationAmount = parseFloat(quote.estDestinationAmount)
      const usdcToDestinationRate = parseFloat(quote.cpnExchangeRate)
      const cadPerUsdc = paytrieData.cadUsdc
      const exchangeRate = cadToDestinationRate(
        cadPerUsdc,
        usdcToDestinationRate
      )
      const cadFee =
        parseFloat(quote.estCpnFee) * cadPerUsdc + paytrieData.cadFee
      const roundedDestinationAmount = Number.isFinite(destinationAmount)
        ? destinationAmount.toFixed(2)
        : null

      isUpdatingFromQuoteRef.current = true

      if (quoteMode === 'fromExact') {
        // fromExact: User inputs CAD amount, we calculate HKD amount
        // Only update receive amount if user is not currently typing in the receive field
        if (activeFieldRef.current !== 'receive') {
          setTransferData((prev) => ({
            ...prev,
            receiveAmount: roundedDestinationAmount ?? prev.receiveAmount,
            fees: cadFee,
            exchangeRate: exchangeRate,
          }))
        } else {
          // User is focused on receive field, but we still need to update fees and exchange rate
          setTransferData((prev) => ({
            ...prev,
            fees: cadFee,
            exchangeRate: exchangeRate,
          }))
        }
      } else if (quoteMode === 'toExact') {
        // toExact: User inputs desired HKD amount, we calculate required CAD amount
        // Only update send amount if user is not currently typing in the send field
        if (activeFieldRef.current !== 'send') {
          const calculatedSendAmount = paytrieData.cadAmount

          setTransferData((prev) => ({
            ...prev,
            sendAmount: Number.isFinite(calculatedSendAmount)
              ? calculatedSendAmount.toFixed(2)
              : prev.sendAmount,
            fees: cadFee,
            exchangeRate: exchangeRate,
          }))
        } else {
          // User is focused on send field, but we still need to update fees and exchange rate
          setTransferData((prev) => ({
            ...prev,
            fees: cadFee,
            exchangeRate: exchangeRate,
          }))
        }
      }

      // Reset the flag after the update
      setTimeout(() => {
        isUpdatingFromQuoteRef.current = false
      }, 0)
    }
  }, [
    quoteData,
    quoteMode,
    transferData.paymentMethod,
    transferData.receiveAmount,
    quoteParams.amount,
  ])

  // Enhanced setTransferData that tracks which field was edited
  const setTransferDataWithTracking = (
    newData:
      | typeof transferData
      | ((prev: typeof transferData) => typeof transferData)
  ) => {
    const updatedData =
      typeof newData === 'function' ? newData(transferData) : newData

    // Skip tracking if this update is from a quote response
    if (isUpdatingFromQuoteRef.current) {
      setTransferData(updatedData)
      return
    }

    // Determine which field was edited and set quote mode
    if (updatedData.sendAmount !== transferData.sendAmount) {
      setQuoteMode('fromExact')
    } else if (updatedData.receiveAmount !== transferData.receiveAmount) {
      setQuoteMode('toExact')
    }

    setTransferData(updatedData)
  }

  // Functions to track active editing
  const setActiveField = (field: 'send' | 'receive' | null) => {
    activeFieldRef.current = field

    if (field === 'send' && quoteMode !== 'fromExact') {
      setQuoteMode('fromExact')
    } else if (field === 'receive' && quoteMode !== 'toExact') {
      setQuoteMode('toExact')
    }
  }

  const cpnQuotes = quoteData?.cpn

  // Find the quote that matches the selected payment method, fallback to first quote
  const selectedQuote =
    cpnQuotes?.find(
      (quote) => quote.paymentMethod === transferData.paymentMethod
    ) || cpnQuotes?.[0]

  return {
    transferData,
    setTransferData: setTransferDataWithTracking,
    setActiveField,
    quote: selectedQuote,
    isQuoteLoading,
    quoteError,
    quoteMode,
    formattedQuote:
      selectedQuote && quoteData?.paytrie
        ? {
            sourceAmount: quoteData.paytrie.cadAmount,
            destinationAmount: parseFloat(selectedQuote.estDestinationAmount),
            exchangeRate: cadToDestinationRate(
              quoteData.paytrie.cadUsdc,
              parseFloat(selectedQuote.cpnExchangeRate)
            ),
            cadUsdcRate: quoteData.paytrie.cadUsdc,
            cpnExchangeRate: parseFloat(selectedQuote.cpnExchangeRate),
            fees: (() => {
              const cpnFeeCad =
                parseFloat(selectedQuote.estCpnFee) *
                  quoteData.paytrie.cadUsdc +
                quoteData.paytrie.cadFee
              return {
                amount: cpnFeeCad,
                currency: 'CAD',
                percentage: (cpnFeeCad / quoteData.paytrie.cadAmount) * 100,
              }
            })(),
            expiresAt: new Date(selectedQuote.quoteExpireDate),
            paymentMethod: selectedQuote.paymentMethod,
            quoteType: quoteMode as 'fromExact' | 'toExact',
            recipientLimits: quoteData.recipientLimits ?? undefined,
          }
        : null,
  }
}

export const useSwapStatus = (intentId: string, enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.swapStatus(intentId),
    queryFn: () => cpnService.getSwapStatus(intentId),
    enabled: enabled && !!intentId,
    refetchInterval: (query) => {
      // Only continue polling if status is still pending/processing
      const data = query?.state?.data
      const status = data?.transactionStatus
      if (!status) return 10000 // Keep polling if no status yet

      // Stop polling if completed, failed, or other final states
      const finalStates = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED']
      if (finalStates.includes(status.toUpperCase())) {
        return false // Stop polling
      }

      return 10000 // Poll every 10 seconds for active transactions
    },
    staleTime: 5000, // Consider stale after 5 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

type CurrentUser = {
  firstName: string | null
  lastName: string | null
  referralCode: string | null
  referralCreditBalance: number
}

export const useUserData = (enabled = true) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.user(),
    queryFn: () =>
      axiosInstance
        .get<{ data: CurrentUser }>('/v2/users/me')
        .then((response) => response.data.data),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - user data doesn't change frequently
  })
}

// Hook for configuration validation
export const useConfigurations = (
  params: {
    sourceCurrency: string
    destinationCountry: string
    destinationCurrency: string
  },
  options?: {
    enabled?: boolean
  }
) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.configurations(params),
    queryFn: () => cpnService.getConfigurations(params),
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000, // 10 minutes - configurations don't change frequently
    retry: 2,
  })
}

// Hook for API-based currency/country/payment method validation
export const useValidateRoute = (
  params: {
    country: string
    currency: string
    paymentMethod: string
  },
  options?: {
    enabled?: boolean
  }
) => {
  const {
    data: configResponse,
    isLoading,
    error,
  } = useConfigurations(
    {
      sourceCurrency: 'USDC', // Always USDC as source
      destinationCountry: params.country,
      destinationCurrency: params.currency,
    },
    {
      enabled: options?.enabled && !!params.country && !!params.currency,
    }
  )

  // Process validation result
  const validationResult = useMemo(() => {
    if (isLoading) {
      return { isLoading: true, isValid: false }
    }

    if (error) {
      return {
        isLoading: false,
        isValid: false,
        reason: 'Failed to validate route - please try again',
      }
    }

    if (!configResponse?.configs) {
      return {
        isLoading: false,
        isValid: false,
        reason: 'No configuration data available',
      }
    }

    // Use the API-based validation function
    return {
      isLoading: false,
      ...validateCurrencyCountryPaymentMethodFromConfigs(
        configResponse.configs,
        params.country,
        params.currency,
        params.paymentMethod
      ),
    }
  }, [
    configResponse,
    isLoading,
    error,
    params.country,
    params.currency,
    params.paymentMethod,
  ])

  return validationResult
}

// Hook for fetching all dynamic supported routes from API
export const useDynamicSupportedRoutes = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: CPN_QUERY_KEYS.allConfigurations(),
    queryFn: async () => {
      const response = await cpnService.getAllConfigurations()
      return transformConfigurationsToDynamicRoutes(response.configurations)
    },
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000, // 10 minutes - configurations don't change frequently
    retry: 2,
  })
}

// Hook for converting USDC amounts to CAD using any valid quote to get the CAD/USDC rate
export const useUsdcToCad = (
  usdcAmount: string,
  options?: {
    enabled?: boolean
    refetchInterval?: number
  }
) => {
  // Use a valid destination to get the paytrie data with CAD/USDC rate
  const quoteParams = {
    quoteType: 'fromExact' as const,
    senderCountry: 'CA',
    sourceCurrency: 'CAD',
    amount: '1000', // Fixed amount just to get the exchange rate
    destinationCountry: 'HK',
    destinationCurrency: 'HKD',
  }

  const {
    data: quoteData,
    isLoading,
    error,
  } = useLatestQuote(quoteParams, {
    enabled: options?.enabled && !!usdcAmount && parseFloat(usdcAmount) > 0,
    refetchInterval: options?.refetchInterval,
  })

  const cadAmount = useMemo(() => {
    if (!quoteData?.paytrie || !usdcAmount) return null
    const usdcValue = parseFloat(usdcAmount)
    if (isNaN(usdcValue)) return null

    // paytrie.cadUsdc = CAD per 1 USDC, so CAD = USDC * cadUsdc
    const cadValue = usdcValue * quoteData.paytrie.cadUsdc
    return cadValue
  }, [quoteData, usdcAmount])

  const exchangeRate = useMemo(() => {
    if (!quoteData?.paytrie) return null
    return 1 / quoteData.paytrie.cadUsdc
  }, [quoteData])

  return {
    cadAmount,
    exchangeRate,
    isLoading,
    error,
    rawData: quoteData,
  }
}
